import { useTranslation } from "react-i18next";
import { Status, Travel } from "@gen/travel/v1/common_pb";
import { i18n } from "@thetreep/ui";

interface PaymentDetailsProps {
  travel: Travel;
}

export function PaymentDetails({ travel }: PaymentDetailsProps) {
  const { t } = useTranslation();

  function formatPrice(price: number | undefined) {
    const formatter = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: travel?.amountDetails?.currency || "EUR",
    });
    return formatter.format(Number(price) / 100);
  }

  const refunded =
    travel.status === Status.CANCELLED ? travel.refundedDetails : undefined;
  const customerRefund = refunded?.refundedOutOfPocketAmount;
  const caisseRefund = refunded?.refundedCaisseAmount;
  const totalRefund = customerRefund ? undefined : refunded?.refundedAmount;

  return (
    <div className="flex flex-col gap-4 mt-4 text-text-strong">
      <div className="flex text-base font-bold lg:text-[24px] font-jakarta justify-between">
        {t("userBookings.paidPrice")}
        <span>{formatPrice(travel?.amountDetails?.outOfPocketAmount)}</span>
      </div>
      {customerRefund && (
        <div className="flex text-base font-bold lg:text-[24px] font-jakarta justify-between">
          {t("userBookings.refundPrice")}
          <span>{formatPrice(Number(customerRefund.amount))}</span>
        </div>
      )}
      {refunded?.customerRefundFailed && (
        <p className="text-sm lg:text-[20px] font-jakarta text-error-strong">
          {t("userBookings.customerRefundFailed", {
            agency: travel.agencyInfo?.alias,
            phone: travel.agencyInfo?.contactPhone,
            hours: travel.agencyInfo?.openingHours,
          })}
        </p>
      )}
      <div className="flex flex-col gap-1">
        <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
          {t("userBookings.initialPrice")}{" "}
          <span>
            {formatPrice(travel?.amountDetails?.includedTaxesTotalAmount)}
          </span>
        </p>
        <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
          {t("userBookings.amountCovered")} (
          {travel?.amountDetails?.coveragePercentage}
          %)
          <span>{formatPrice(travel?.amountDetails?.coveredAmount)}</span>
        </p>
        <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
          {t("userBookings.agencyFees")}
          <span>
            {formatPrice(travel?.amountDetails?.includedTaxesAgencyFees)}
          </span>
        </p>
        {totalRefund && (
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("userBookings.refundedTotal")}
            <span>{formatPrice(Number(totalRefund.amount))}</span>
          </p>
        )}
        {caisseRefund && (
          <p className="flex text-sm lg:text-[20px] font-jakarta justify-between">
            {t("userBookings.refundPriceToCaisse")}
            <span>{formatPrice(Number(caisseRefund.amount))}</span>
          </p>
        )}
      </div>
    </div>
  );
}
